import React, { useContext, useState } from 'react';
import { CompanyRoleAssignmentContext } from '../../../../../../context/CompanyRoleAssignmentContext';
import { UserContext } from '../../../../../../context/UserContext';
import {
  UpdateCompanyRoleAssignmentResponse,
  UpdateCompanyRoleAssignmentVariables,
  CompanyRoleAssignmentMutations,
} from '../../../../../../graphql/companyRoleAssignment.graphql';
import useMutation from '../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../providers/ToastProvider';
import {
  CompanyRole,
  CompanyRoleAssignment,
  CompanyRoleAssignmentInput,
  CompanyRoleAssignmentUtil,
} from '../../../../../../types/company_role_assignment';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { User } from '../../../../../../types/user';
import { toMap } from '../../../../../../util/map.util';
import { Button, ButtonVariant } from '../../../../../components/Button';
import SaveChangesButton from '../../../../../components/Button/templates/SaveChangesButton';
import { DropdownSelect } from '../../../../../components/Dropdown';
import { ProfileAvatar } from '../../../../../components/Profile';
import { Surface } from '../../../../../components/Surface';
import { ToastType } from '../../../../../components/Toast';
import UserStockLocationRolesView from '../../Views/UserStockLocationRolesView';
import DeleteCompanyRoleAssignmentModal from './DeleteCompanyRoleAssignmentModal';

interface UserUpdateRolesPaneProps {
  user: User;
  companyRole: CompanyRoleAssignment;
}

export default function UserUpdateRolesPane({ user, companyRole }: UserUpdateRolesPaneProps) {
  const { showToast } = useToast();

  const { currentUser } = useContext(UserContext);
  const { companyRoles, setCompanyRoles } = useContext(CompanyRoleAssignmentContext);

  const [input, setInput] = useState(new CompanyRoleAssignmentInput(companyRole));
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [update, { loading }] = useMutation<UpdateCompanyRoleAssignmentResponse, UpdateCompanyRoleAssignmentVariables>(
    CompanyRoleAssignmentMutations.update,
    {
      variables: { companyRoleAssignment: input.forUpdate() },
      onCompleted: response => {
        const userId = response.updateCompanyRoleAssignment.userId;
        const forUser = toMap(companyRoles.get(userId) || [], 'id').set(
          response.updateCompanyRoleAssignment.id,
          new CompanyRoleAssignment(response.updateCompanyRoleAssignment),
        );
        setInput(new CompanyRoleAssignmentInput(response.updateCompanyRoleAssignment));
        setCompanyRoles(new Map(companyRoles).set(userId, [...forUser.values()]));
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.userUpdatedSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  return (
    <div className='flex flex-grow flex-col gap-8'>
      {deleteModalOpen && (
        <DeleteCompanyRoleAssignmentModal assignment={input} open={deleteModalOpen} setOpen={setDeleteModalOpen} />
      )}
      <Surface>
        <div className='flex gap-16'>
          <div className='flex items-center gap-4'>
            <ProfileAvatar size={'lg'} user={user} />
            <div>
              <p className='section-title'>{user.fullName}</p>
              <p className='subtitle'>{user.email}</p>
            </div>
          </div>
          <div className='grid gap-4'>
            <DropdownSelect<CompanyRole>
              label={t(TranslationKey.role).singular.upper}
              items={Object.values(CompanyRole)}
              value={input.role}
              textBuilder={item => CompanyRoleAssignmentUtil.roleToString(item)}
              onSelect={item => setInput(input.withRole(item))}
            />
            <div className='flex gap-2'>
              <SaveChangesButton
                loading={loading}
                onClick={() => update()}
                disabled={companyRole.role === input.role}
              />
              <Button
                disabled={loading}
                variant={ButtonVariant.danger}
                onClick={() => setDeleteModalOpen(true)}
                text={
                  t(user.id === currentUser?.id ? TranslationKey.leave : TranslationKey.removeFromCompany).singular
                    .upper
                }
              />
            </div>
          </div>
        </div>
      </Surface>
      {input.role !== CompanyRole.administrator && <UserStockLocationRolesView companyRole={companyRole} user={user} />}
    </div>
  );
}
